import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import * as XLSX from 'xlsx';
import { FiUpload, FiX, FiCheckCircle, FiAlertCircle } from 'react-icons/fi';
import axios from 'axios';
import { BASE_URL, API_URLS } from '../constants/config';
import { getTokensFromStore, checkIfTokenExpired } from '../utilities/tokens';
import { refreshTokens } from '../actions/usersActions';
import { parseBirthday, formatRuDate, sameDate } from '../utilities/excelDate';
import EditPlayerModal from './EditPlayerModal';
import '../styles/importPlayersFromExcel.css';

// Проверка и добавление — batch-руты searchMany / importToApplication
// (FRONTEND_API_CHANGES §2.3). Старый путь create+connect оставляем только
// для новых игроков с фото в карточке: importToApplication фото не принимает.
const REQUEST_TIMEOUT_MS = 60000;
const RETRY_ATTEMPTS = 3;
const RETRY_DELAY_MS = 1500;
const MAX_PHOTO_BYTES = 5 * 1024 * 1024;
const CANDIDATES_SHOWN = 5;

const POSITIONS = [
    { id: 1, label: 'Нападающий' },
    { id: 2, label: 'Полузащитник' },
    { id: 3, label: 'Защитник' },
    { id: 4, label: 'Вратарь' },
    { id: 5, label: 'Универсал' },
];
const positionLabel = (id) => (POSITIONS.find(p => p.id === Number(id)) || POSITIONS[4]).label;
const positionIdByLabel = (label) => (POSITIONS.find(p => p.label === label) || POSITIONS[4]).id;

// Буквы, пробел, дефис, апостроф, точка: «Мамедов-оглы», «Д'Артаньян».
// Цифры и прочее обычно значат, что выбрана не та колонка
const NAME_RE = /^[A-Za-zА-Яа-яЁё]+(?:[ '’.-]+[A-Za-zА-Яа-яЁё]+)*\.?$/;

const cleanText = (value) => String(value ?? '').trim().replace(/\s+/g, ' ');
const normalizeName = (value) => cleanText(value).toLowerCase().replace(/ё/g, 'е');
const todayIso = () => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
};

const playerIdOf = (p) => Number(p.id || p.player_id || p.playerId) || null;
const birthdayOf = (p) => {
    const value = p.birthday || p.birth_date || p.birthDate || p.date_of_birth;
    return value ? String(value).slice(0, 10) : null;
};
const photoOf = (p) => p.photo || p.image || p.avatar || p.photo_url || p.image_url || null;

const wait = (ms) => new Promise(resolve => setTimeout(resolve, ms));

// Обрыв соединения и таймаут — единственное, что имеет смысл повторять.
// Ответ сервера с ошибкой (400, 401, 500) повторять бессмысленно
const isTransportFailure = (error) => (
    !error.response
    && (error.code === 'ECONNABORTED' || error.message === 'Network Error' || !error.code)
);

// Одиночный запрос без повтора — для всего, что меняет базу
const postOnce = (url, body, config) => axios.post(url, body, { ...config, timeout: REQUEST_TIMEOUT_MS });

const postWithRetry = async (url, body, config) => {
    let lastError = null;
    for (let attempt = 1; attempt <= RETRY_ATTEMPTS; attempt++) {
        try {
            return await axios.post(url, body, { ...config, timeout: REQUEST_TIMEOUT_MS });
        } catch (error) {
            lastError = error;
            if (!isTransportFailure(error) || attempt === RETRY_ATTEMPTS) throw error;
            await wait(RETRY_DELAY_MS * attempt);
        }
    }
    throw lastError;
};

// Человекочитаемая причина вместо голого "Network Error"
// Сервер, упавший на запросе, отвечает без CORS-заголовков, и браузер
// показывает это как "Network Error" без ответа — так же, как настоящий обрыв.
// Поэтому формулировка честная: ответа не было, причина на стороне сервера или сети
const describeRequestError = (error) => {
    if (!error.response) {
        if (error.code === 'ECONNABORTED') {
            return `сервер не ответил за ${REQUEST_TIMEOUT_MS / 1000} с`;
        }
        return 'сервер не прислал ответ (запрос упал на сервере или связь оборвалась)';
    }
    const data = error.response.data;
    if (typeof data === 'string' && data && data.length < 300) return data;
    if (data && (data.message || data.error)) return data.message || data.error;
    return `ошибка сервера ${error.response.status}`;
};

const authHeaders = (tokens) => ({
    headers: {
        "Authorization": "Bearer " + tokens.access_token,
        'Accept': 'application/json',
        'Content-Type': 'application/json',
    }
});

const mapCandidate = (p) => ({
    id: playerIdOf(p),
    family: cleanText(p.family),
    name: cleanText(p.name),
    father_name: cleanText(p.father_name),
    birthday: birthdayOf(p),
    image: photoOf(p),
});

// Все игроки базы с такими же фамилией и именем (без учёта регистра и ё).
// Совпадение ФИО ещё не значит, что это тот же человек — выбирает админ
const searchNamesakes = async (family, name, tokens) => {
    const result = await postWithRetry(
        BASE_URL.url + API_URLS.searchPlayersByParams,
        { family, name },
        authHeaders(tokens)
    );
    const list = Array.isArray(result.data) ? result.data
        : Array.isArray(result.data?.data) ? result.data.data : [];
    const seen = new Set();
    return list
        .filter(p => normalizeName(p.family) === normalizeName(family)
            && normalizeName(p.name) === normalizeName(name))
        .map(mapCandidate)
        .filter(c => c.id && !seen.has(c.id) && seen.add(c.id));
};

// Массовый поиск кандидатов для шага проверки Excel
const searchNamesakesMany = async (rows, tokens) => {
    const payload = {
        players: rows.map(row => ({
            key: String(row.key),
            family: row.family,
            name: row.name,
            birthday: row.birth_date || undefined,
        })),
    };
    const result = await postWithRetry(
        BASE_URL.url + API_URLS.searchPlayersMany,
        payload,
        authHeaders(tokens)
    );
    const list = Array.isArray(result.data) ? result.data
        : Array.isArray(result.data?.data) ? result.data.data : [];
    const byKey = new Map();
    list.forEach(item => {
        const seen = new Set();
        const req = rows.find(r => String(r.key) === String(item.key));
        const candidates = (item.candidates || [])
            .filter(p => !req || (
                normalizeName(p.family) === normalizeName(req.family)
                && normalizeName(p.name) === normalizeName(req.name)
            ))
            .map(mapCandidate)
            .filter(c => c.id && !seen.has(c.id) && seen.add(c.id));
        byKey.set(String(item.key), candidates);
    });
    return byKey;
};

// Кандидаты: сначала совпавшие по дате рождения, потом по отчеству
const rankCandidates = (candidates, birthday, fatherName) => {
    const father = normalizeName(fatherName);
    const score = (c) => (sameDate(c.birthday, birthday) ? 2 : 0)
        + (father && normalizeName(c.father_name) === father ? 1 : 0);
    return candidates
        .map(c => ({ ...c, birthdayMatch: sameDate(c.birthday, birthday), score: score(c) }))
        .sort((a, b) => b.score - a.score || b.id - a.id);
};

// Кого выбрать по умолчанию: только того, у кого совпала дата рождения
// и не противоречит отчество. Иначе — «Создать нового», админ решит сам
const defaultChoice = (candidates, fatherName) => {
    const father = normalizeName(fatherName);
    const match = candidates.find(c => c.birthdayMatch
        && (!father || !c.father_name || normalizeName(c.father_name) === father));
    return match ? match.id : 'new';
};

const readAsBase64 = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
        const base64 = String(reader.result || '').split(',')[1];
        if (!base64 || base64.length < 1000) {
            reject(new Error('фото повреждено или слишком маленькое'));
            return;
        }
        resolve(base64);
    };
    reader.onerror = () => reject(new Error('не удалось прочитать фото'));
    reader.readAsDataURL(file);
});

export default function ImportPlayersFromExcel({ 
    isOpen, 
    onClose, 
    onSuccess,
    applicationId,
    application,
    team,
    teamId,
    occupiedNumbers = [],
    userData = {}
}) {
    const dispatch = useDispatch();
    
    const [step, setStep] = useState(1); // 1: загрузка файла, 2: маппинг колонок, 3: проверка и подтверждение
    const [excelData, setExcelData] = useState([]);
    const [headers, setHeaders] = useState([]);
    const [hasHeaders, setHasHeaders] = useState(true); // Первая строка содержит заголовки
    // Файлы, сохранённые на Mac, могут считать даты от 1904 года
    const [date1904, setDate1904] = useState(false);
    const [columnMapping, setColumnMapping] = useState({
        family: '',
        name: '',
        father_name: '',
        birth_date: '',
        number: '',
        position: ''
    });
    const [processedPlayers, setProcessedPlayers] = useState([]);
    const [isProcessing, setIsProcessing] = useState(false);
    const [isAdding, setIsAdding] = useState(false);
    // Ход обработки и добавления: «3 из 12»
    const [progress, setProgress] = useState(null);
    // Открытая карточка нового игрока: индекс строки и данные для формы
    const [card, setCard] = useState(null);
    // Строки, у которых раскрыт полный список однофамильцев
    const [expanded, setExpanded] = useState({});
    const [bulkPosition, setBulkPosition] = useState(1);

    // Функция для определения позиции по названию
    const getPositionId = (positionText) => {
        if (!positionText) return 5; // Универсал по умолчанию

        const text = positionText.toLowerCase().trim();

        // Нападающий (1)
        if (text.includes('напад') || text.includes('форвард') || text.includes('нап')) {
            return 1;
        }
        // Полузащитник (2)
        if (text.includes('полузащ') || text.includes('хавбек') || text.includes('полу') || text.includes('пз')) {
            return 2;
        }
        // Защитник (3)
        if (text.includes('защ') || text.includes('защит') || text.includes('деф')) {
            return 3;
        }
        // Вратарь (4)
        if (text.includes('врат') || text.includes('голкипер') || text.includes('вр') || text.includes('гк')) {
            return 4;
        }

        // Универсал (5) - если не удалось определить
        return 5;
    };

    const handleFileUpload = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = (event) => {
            try {
                const workbook = XLSX.read(event.target.result, { type: 'binary' });
                setDate1904(Boolean(workbook.Workbook && workbook.Workbook.WBProps && workbook.Workbook.WBProps.date1904));
                const sheetName = workbook.SheetNames[0];
                const worksheet = workbook.Sheets[sheetName];
                const data = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

                if (data.length === 0) {
                    alert('Файл пустой или неправильного формата');
                    return;
                }

                // Если первая строка содержит заголовки - используем их
                // Если нет - генерируем автоматически
                let headers, excelRows;
                
                if (hasHeaders) {
                    // Первая строка - заголовки
                    headers = data[0].map((header, index) => ({
                        letter: String.fromCharCode(65 + index), // A, B, C...
                        value: header || `Колонка ${String.fromCharCode(65 + index)}`,
                        index: index
                    }));
                    excelRows = data.slice(1); // Остальные строки - данные
                } else {
                    // Все строки - данные, генерируем заголовки
                    const columnCount = data[0].length;
                    headers = Array.from({ length: columnCount }, (_, index) => ({
                        letter: String.fromCharCode(65 + index),
                        value: `Колонка ${String.fromCharCode(65 + index)}`,
                        index: index
                    }));
                    excelRows = data; // Все строки - данные
                }

                setHeaders(headers);
                setExcelData(excelRows);
                setStep(2);
            } catch (error) {
                console.error('Error parsing Excel:', error);
                alert('Ошибка при чтении файла');
            }
        };
        reader.readAsBinaryString(file);
    };

    const handleColumnMappingChange = (field, columnIndex) => {
        setColumnMapping(prev => ({
            ...prev,
            [field]: columnIndex
        }));
    };

    const updateRow = (index, patch) => {
        setProcessedPlayers(prev => prev.map((p, i) => (i === index ? { ...p, ...patch } : p)));
    };

    // Игроки, которые уже стоят в этой заявке: второй раз их не добавляем
    const inApplicationIds = new Set(
        ((application && application.data) || []).map(p => Number(p.player_id)).filter(Boolean)
    );

    // Проверка одной строки по уже полученным кандидатам (или одиночный поиск)
    const applyCandidates = (row, found) => {
        const candidates = rankCandidates(found, row.birth_date, row.father_name);
        return {
            ...row,
            candidates,
            choice: defaultChoice(candidates, row.father_name),
            checkFailed: false,
            checkError: null,
        };
    };

    const checkRow = async (row, tokens) => {
        try {
            const found = await searchNamesakes(row.family, row.name, tokens);
            return applyCandidates(row, found);
        } catch (error) {
            console.error('Error searching player:', error);
            // Поиск не отработал — молча считать игрока новым нельзя:
            // так создаётся дубликат того, кто уже есть в базе
            return {
                ...row,
                candidates: [],
                choice: 'new',
                checkFailed: true,
                checkError: describeRequestError(error),
            };
        }
    };

    const handleProcessPlayers = async () => {
        // Проверка обязательных полей
        if (!columnMapping.family || !columnMapping.name || !columnMapping.birth_date) {
            alert('Укажите обязательные колонки: Фамилия, Имя, Дата рождения');
            return;
        }
        // Одна колонка на два поля — почти наверняка ошибка выбора
        const usedColumns = Object.values(columnMapping).filter(v => v !== '');
        if (new Set(usedColumns).size !== usedColumns.length) {
            alert('Одна и та же колонка выбрана для нескольких полей. Проверьте соответствие колонок.');
            return;
        }

        const rows = [];
        excelData.forEach((row, rowIndex) => {
            if (!row || row.length === 0) return;
            const cell = (field) => (columnMapping[field] !== '' ? row[columnMapping[field]] : '');
            const family = cleanText(cell('family'));
            const name = cleanText(cell('name'));
            // Пустые строки пропускаем
            if (!family && !name) return;
            const rawBirth = cell('birth_date');
            rows.push({
                key: rowIndex,
                // Номер строки как в Excel — чтобы админ нашёл её в файле
                excelRow: rowIndex + (hasHeaders ? 2 : 1),
                family,
                name,
                father_name: cleanText(cell('father_name')),
                // Дата рождения: число Excel или строка в любом привычном формате → ГГГГ-ММ-ДД.
                // Исходное значение храним, чтобы показать админу, если разобрать не вышло
                birth_date: parseBirthday(rawBirth, { date1904 }),
                birth_date_raw: rawBirth ?? '',
                position_id: getPositionId(cleanText(cell('position'))),
                editableNumber: cleanText(cell('number')),
                isSelected: true,
                draft: null,
                createdPlayerId: null,
                added: false,
                addError: null,
            });
        });

        if (rows.length === 0) {
            alert('В выбранных колонках нет ни одного игрока');
            return;
        }

        setIsProcessing(true);
        try {
            if (checkIfTokenExpired()) {
                await dispatch(refreshTokens());
            }
            const tokens = getTokensFromStore();
            setProgress({ done: 0, total: rows.length });

            const searchable = rows.filter(r => r.family && r.name);
            let byKey = new Map();
            try {
                byKey = await searchNamesakesMany(searchable, tokens);
            } catch (error) {
                console.error('searchMany failed, falling back to per-row search:', error);
                const processed = [];
                for (let i = 0; i < rows.length; i++) {
                    const row = rows[i];
                    processed.push(row.family && row.name
                        ? await checkRow(row, tokens)
                        : { ...row, candidates: [], choice: 'new', checkFailed: false });
                    setProgress({ done: i + 1, total: rows.length });
                }
                setProcessedPlayers(processed.map(p => (p.checkFailed ? { ...p, isSelected: false } : p)));
                setExpanded({});
                setStep(3);
                return;
            }

            const processed = rows.map(row => {
                if (!row.family || !row.name) {
                    return { ...row, candidates: [], choice: 'new', checkFailed: false };
                }
                const found = byKey.get(String(row.key));
                if (found === undefined) {
                    return {
                        ...row,
                        candidates: [],
                        choice: 'new',
                        checkFailed: true,
                        checkError: 'нет ответа поиска для строки',
                    };
                }
                return applyCandidates(row, found);
            });

            // Не проверенных по базе не отмечаем, чтобы случайно не создать дубликат
            setProcessedPlayers(processed.map(p => (p.checkFailed ? { ...p, isSelected: false } : p)));
            setExpanded({});
            setStep(3);
        } catch (error) {
            console.error('Error processing players:', error);
            alert('Ошибка при обработке данных: ' + (error.message || 'Неизвестная ошибка'));
        } finally {
            setIsProcessing(false);
            setProgress(null);
        }
    };

    const handleRecheck = async (index, rowOverride = null) => {
        const row = rowOverride || processedPlayers[index];
        updateRow(index, { rechecking: true });
        try {
            if (checkIfTokenExpired()) {
                await dispatch(refreshTokens());
            }
            const checked = await checkRow(row, getTokensFromStore());
            updateRow(index, {
                candidates: checked.candidates,
                // Если админ уже заполнил карточку нового — выбор не трогаем
                choice: row.draft ? 'new' : checked.choice,
                checkFailed: checked.checkFailed,
                checkError: checked.checkError,
                isSelected: checked.checkFailed ? row.isSelected : true,
                rechecking: false,
            });
        } catch (error) {
            updateRow(index, { rechecking: false, checkFailed: true, checkError: describeRequestError(error) });
        }
    };

    const handleTogglePlayer = (index) => {
        setProcessedPlayers(prev => prev.map((player, i) =>
            i === index ? { ...player, isSelected: !player.isSelected } : player
        ));
    };

    const handleNumberChange = (index, newNumber) => {
        // Ограничиваем ввод двумя цифрами
        if (newNumber.length > 2) {
            newNumber = newNumber.slice(0, 2);
        }
        setProcessedPlayers(prev => prev.map((player, i) =>
            i === index ? { ...player, editableNumber: newNumber } : player
        ));
    };

    const applyBulkPosition = () => {
        setProcessedPlayers(prev => prev.map(p => (
            p.choice === 'new' && !p.added ? { ...p, position_id: Number(bulkPosition) } : p
        )));
    };

    // Карточка нового игрока — та же форма, что при обычном создании,
    // с уже заполненными из Excel полями. Объект initialData создаётся
    // один раз при открытии, иначе форма сбрасывалась бы при каждом рендере
    const openCard = (index) => {
        const row = processedPlayers[index];
        setCard({
            index,
            initialData: {
                family: row.family,
                name: row.name,
                father_name: row.father_name,
                birthday: row.birth_date || '',
                position: positionLabel(row.position_id),
                ...(row.draft || {}),
            },
        });
    };

    const handleDraftSave = (draft) => {
        const index = card.index;
        const before = processedPlayers[index];
        const patch = {
            family: cleanText(draft.family),
            name: cleanText(draft.name),
            father_name: cleanText(draft.father_name),
            birth_date: draft.birthday || null,
            birth_date_raw: draft.birthday || '',
            position_id: positionIdByLabel(draft.position),
            draft: {
                nationality: draft.nationality,
                height: draft.height,
                weight: draft.weight,
                working_foot: draft.working_foot,
                photoFile: draft.photoFile || null,
                photoPreview: draft.photoPreview || null,
            },
            choice: 'new',
            isSelected: true,
            candidates: rankCandidates(before.candidates || [], draft.birthday, draft.father_name),
        };
        updateRow(index, patch);
        // Поменяли фамилию или имя — однофамильцы в базе уже другие
        if (normalizeName(patch.family) !== normalizeName(before.family)
            || normalizeName(patch.name) !== normalizeName(before.name)) {
            handleRecheck(index, { ...before, ...patch });
        }
    };

    // Все ошибки строки — до отправки. Пустой массив — строку можно добавлять
    const validateRow = (row, index, rows) => {
        const errors = [];
        if (!row.isSelected || row.added) return errors;
        const isNew = row.choice === 'new';
        const isActive = (other, i) => i !== index && other.isSelected && !other.added;

        const checkName = (value, label, required) => {
            if (!value) {
                if (required) errors.push(`не заполнено поле «${label}»`);
                return;
            }
            if (value.length > 100) errors.push(`«${label}» длиннее 100 символов`);
            else if (isNew && !NAME_RE.test(value)) errors.push(`«${label}»: недопустимые символы в «${value}»`);
        };
        checkName(row.family, 'Фамилия', true);
        checkName(row.name, 'Имя', true);

        if (isNew) {
            checkName(row.father_name, 'Отчество', false);

            if (!row.birth_date) {
                errors.push(row.birth_date_raw !== '' && row.birth_date_raw !== null
                    ? `не распознана дата рождения «${String(row.birth_date_raw)}»`
                    : 'нет даты рождения');
            } else if (row.birth_date < '1900-01-01' || row.birth_date > todayIso()) {
                errors.push(`недопустимая дата рождения ${formatRuDate(row.birth_date)}`);
            }

            if (row.checkFailed) errors.push('не проверен по базе');
            if (!POSITIONS.some(p => p.id === Number(row.position_id))) errors.push('не выбрана позиция');

            const draft = row.draft || {};
            if (draft.photoFile && draft.photoFile.size > MAX_PHOTO_BYTES) errors.push('фото больше 5 МБ');
            const outOfRange = (value, min, max) => value !== '' && value !== null && value !== undefined
                && (!/^\d+$/.test(String(value)) || Number(value) < min || Number(value) > max);
            if (outOfRange(draft.height, 100, 250)) errors.push(`рост «${draft.height}»: нужно число от 100 до 250 см`);
            if (outOfRange(draft.weight, 30, 200)) errors.push(`вес «${draft.weight}»: нужно число от 30 до 200 кг`);

            const twin = rows.findIndex((other, i) => isActive(other, i)
                && other.choice === 'new'
                && normalizeName(other.family) === normalizeName(row.family)
                && normalizeName(other.name) === normalizeName(row.name)
                && normalizeName(other.father_name) === normalizeName(row.father_name)
                && sameDate(other.birth_date, row.birth_date));
            if (twin !== -1) errors.push(`этот же игрок повторяется в строке ${rows[twin].excelRow}`);
        } else {
            const twin = rows.findIndex((other, i) => isActive(other, i) && String(other.choice) === String(row.choice));
            if (twin !== -1) errors.push(`этот же игрок из базы выбран в строке ${rows[twin].excelRow}`);
        }

        const playerId = isNew ? row.createdPlayerId : Number(row.choice);
        if (playerId && inApplicationIds.has(playerId)) {
            errors.push('этот игрок уже есть в заявке — снимите галочку');
        }

        const number = String(row.editableNumber ?? '').trim();
        if (number) {
            if (!/^\d{1,2}$/.test(number)) {
                errors.push(`номер «${number}»: нужно целое число от 0 до 99`);
            } else {
                if (occupiedNumbers.map(Number).includes(Number(number))) {
                    errors.push(`номер ${number} уже занят в заявке`);
                }
                const twin = rows.findIndex((other, i) => isActive(other, i)
                    && String(other.editableNumber ?? '').trim() !== ''
                    && Number(other.editableNumber) === Number(number));
                if (twin !== -1) errors.push(`номер ${number} повторяется в строке ${rows[twin].excelRow}`);
            }
        }
        return errors;
    };

    // Новый игрок появился в базе? Нужен, когда сервер не вернул id
    // или ответ потерялся — повторно создавать нельзя, только искать
    const findCreatedId = async (row, excludeIds, tokens) => {
        const found = await searchNamesakes(row.family, row.name, tokens);
        const fresh = found.filter(c => !excludeIds.has(c.id)
            && sameDate(c.birthday, row.birth_date)
            && normalizeName(c.father_name) === normalizeName(row.father_name));
        return fresh.length ? Math.max(...fresh.map(c => c.id)) : null;
    };

    // Тело запроса повторяет карточку создания игрока (EditPlayerModal)
    const createPlayer = async (row, tokens, clientOwner, excludeIds) => {
        const draft = row.draft || {};
        const image = draft.photoFile ? await readAsBase64(draft.photoFile) : false;
        const payload = {
            family: row.family,
            name: row.name,
            // Строкой, как в карточке создания: null сервер не принимает
            father_name: row.father_name || '',
            position_id: Number(row.position_id) || 5,
            weight: draft.weight ? parseInt(draft.weight, 10) : null,
            height: draft.height ? parseInt(draft.height, 10) : null,
            nationality: draft.nationality || 'Россия',
            work_foot: draft.working_foot || 'Правая',
            birthday: row.birth_date,
            age: new Date().getFullYear() - Number(row.birth_date.slice(0, 4)),
            image,
            client_owner: clientOwner,
        };

        let response;
        try {
            response = await postOnce(BASE_URL.url + '/players/add', payload, authHeaders(tokens));
        } catch (error) {
            // Сервер мог успеть создать игрока и упасть уже после — проверяем
            const createdId = await findCreatedId(row, excludeIds, tokens).catch(() => null);
            if (createdId) return createdId;
            throw error;
        }

        const data = response.data;
        if (data === false) {
            throw new Error('сервер отказался создавать игрока');
        }
        const idFromResponse = data && typeof data === 'object'
            ? Number(data.id || data.player_id || data.playerId || data.insertId
                || data.data?.id || data.data?.player_id) || null
            : null;
        if (idFromResponse) return idFromResponse;

        // Сервер отвечает просто true — находим созданного поиском
        const createdId = await findCreatedId(row, excludeIds, tokens);
        if (!createdId) throw new Error('сервер ответил, но созданный игрок не найден в базе');
        return createdId;
    };

    const connectToApplication = (playerId, row, tokens) => {
        const number = String(row.editableNumber ?? '').trim();
        const teamData = {
            ...team,
            player_number: number === '' ? null : Number(number),
            application_id: applicationId,
            application_name: application?.application_name || application?.label || '',
            application_comment: application?.application_comment || '',
            application_year: application?.year || application?.application_year || new Date().getFullYear()
        };
        return postOnce(
            BASE_URL.url + '/players/connectToApplication',
            { playerId, add_data: [teamData], remove_data: [] },
            authHeaders(tokens)
        );
    };

    const handleAddPlayers = async () => {
        const queue = processedPlayers
            .map((row, index) => ({ row, index }))
            .filter(({ row }) => row.isSelected && !row.added);

        if (queue.length === 0) {
            alert('Выберите хотя бы одного игрока');
            return;
        }
        if (!applicationId) {
            alert('Не выбрана заявка, в которую добавлять игроков');
            return;
        }
        const invalid = queue.filter(({ row, index }) => validateRow(row, index, processedPlayers).length > 0);
        if (invalid.length > 0) {
            alert(`Исправьте ошибки в строках: ${invalid.map(({ row }) => row.excelRow).join(', ')}. Ничего не отправлено.`);
            return;
        }

        const clientOwner = userData?.role === 7 ? 0 : (userData?.client_id || 0);
        const excludeIds = new Set();
        processedPlayers.forEach(p => {
            (p.candidates || []).forEach(c => excludeIds.add(c.id));
            if (p.createdPlayerId) excludeIds.add(p.createdPlayerId);
        });

        // importToApplication принимает только ФИО, дату рождения и амплуа. Новых
        // игроков, у которых в карточке заполнено что-то ещё (фото, рост, вес,
        // гражданство, рабочая нога), создаём старым путём, чтобы это не потерялось
        const needsPhotoCreate = ({ row }) => row.choice === 'new'
            && !row.createdPlayerId
            && Boolean(row.draft && (
                row.draft.photoFile
                || row.draft.height
                || row.draft.weight
                || row.draft.nationality
                || row.draft.working_foot
            ));

        setIsAdding(true);
        let addedCount = 0;
        let failedCount = 0;

        try {
            if (checkIfTokenExpired()) {
                await dispatch(refreshTokens());
            }
            const tokens = getTokensFromStore();
            setProgress({ done: 0, total: queue.length });

            const photoQueue = queue.filter(needsPhotoCreate);
            const batchQueue = queue.filter(item => !needsPhotoCreate(item));

            for (let k = 0; k < photoQueue.length; k++) {
                const { row, index } = photoQueue[k];
                updateRow(index, { addError: null });
                let playerId = row.createdPlayerId;
                try {
                    if (!playerId) {
                        playerId = await createPlayer(row, tokens, clientOwner, excludeIds);
                        excludeIds.add(playerId);
                        updateRow(index, { createdPlayerId: playerId });
                    }
                    await connectToApplication(playerId, row, tokens);
                    addedCount++;
                    updateRow(index, { added: true, addError: null });
                } catch (error) {
                    failedCount++;
                    const reason = error.isAxiosError ? describeRequestError(error) : error.message;
                    updateRow(index, { addError: reason });
                }
                setProgress({ done: k + 1, total: queue.length });
            }

            if (batchQueue.length > 0) {
                const importRows = batchQueue.map(({ row }) => {
                    const number = String(row.editableNumber ?? '').trim();
                    const playerNumber = number === '' ? null : Number(number);
                    const key = String(row.key);
                    if (row.choice === 'new') {
                        if (row.createdPlayerId) {
                            return { key, player_id: row.createdPlayerId, player_number: playerNumber };
                        }
                        return {
                            key,
                            player_number: playerNumber,
                            new_player: {
                                family: row.family,
                                name: row.name,
                                father_name: row.father_name || '',
                                birthday: row.birth_date,
                                position_id: Number(row.position_id) || 5,
                            },
                        };
                    }
                    return {
                        key,
                        player_id: Number(row.choice),
                        player_number: playerNumber,
                    };
                });

                try {
                    const response = await postOnce(
                        BASE_URL.url + API_URLS.importPlayersToApplication,
                        {
                            application_id: applicationId,
                            team_id: teamId || team?.id || team?.team_id,
                            rows: importRows,
                        },
                        authHeaders(tokens)
                    );
                    const results = Array.isArray(response.data) ? response.data
                        : Array.isArray(response.data?.data) ? response.data.data : [];
                    const byKey = new Map(results.map(r => [String(r.key), r]));

                    batchQueue.forEach(({ row, index }) => {
                        const result = byKey.get(String(row.key));
                        if (!result) {
                            failedCount++;
                            updateRow(index, { addError: 'нет ответа сервера для строки' });
                            return;
                        }
                        if (result.status === 'error' || result.error) {
                            failedCount++;
                            updateRow(index, { addError: result.error || 'ошибка импорта' });
                            return;
                        }
                        addedCount++;
                        const playerId = result.player_id || null;
                        updateRow(index, {
                            added: true,
                            addError: null,
                            createdPlayerId: playerId || row.createdPlayerId,
                        });
                        if (playerId) excludeIds.add(playerId);
                    });
                } catch (error) {
                    console.error('importToApplication failed:', error);
                    batchQueue.forEach(({ index }) => {
                        failedCount++;
                        updateRow(index, { addError: describeRequestError(error) });
                    });
                }
            }
        } catch (error) {
            console.error('Error adding players:', error);
            alert('Критическая ошибка при добавлении игроков: ' + (error.message || 'Неизвестная ошибка'));
        } finally {
            setIsAdding(false);
            setProgress(null);
        }

        if (addedCount > 0) {
            onSuccess();
        }
        if (failedCount === 0 && addedCount > 0) {
            alert(`✅ Успешно добавлено игроков: ${addedCount}`);
            handleClose(true);
        } else if (failedCount > 0) {
            alert(`Добавлено: ${addedCount} из ${queue.length}.\n\n`
                + `Не получилось: ${failedCount} — причина указана в строке игрока. `
                + 'Добавленные отмечены и повторно не отправятся.');
        }
    };

    const handleClose = (force = false) => {
        // Посреди отправки не закрываем: иначе непонятно, кто успел добавиться
        if (isAdding && force !== true) return;
        setStep(1);
        setExcelData([]);
        setHeaders([]);
        setHasHeaders(true);
        setColumnMapping({
            family: '',
            name: '',
            father_name: '',
            birth_date: '',
            number: '',
            position: ''
        });
        setProcessedPlayers([]);
        setIsProcessing(false);
        setIsAdding(false);
        setProgress(null);
        setCard(null);
        setExpanded({});
        onClose();
    };

    if (!isOpen) return null;

    return (
        <>
        <div className="modalOverlay" onClick={(e) => e.target.className === 'modalOverlay' && handleClose()}>
            <div className="importExcelModalContent">
                <div className="modalHeader">
                    <h2>Импорт игроков из Excel</h2>
                    <button className="closeButton" onClick={handleClose}>
                        <FiX />
                    </button>
                </div>

                <div className="modalBody">
                    {step === 1 && (
                        <div className="uploadSection">
                            <div className="uploadInstructions">
                                <h3>Шаг 1: Загрузите файл Excel</h3>
                                <p>Файл должен содержать следующие данные об игроках:</p>
                                <ul>
                                    <li><strong>Фамилия</strong> (обязательно)</li>
                                    <li><strong>Имя</strong> (обязательно)</li>
                                    <li><strong>Дата рождения</strong> (обязательно, формат: ДД.ММ.ГГГГ)</li>
                                    <li>Отчество</li>
                                    <li>Игровой номер</li>
                                    <li>Позиция</li>
                                </ul>
                            </div>
                            
                            <div className="headerCheckboxSection">
                                <label className="headerCheckbox">
                                    <input
                                        type="checkbox"
                                        checked={hasHeaders}
                                        onChange={(e) => setHasHeaders(e.target.checked)}
                                    />
                                    <span>Первая строка содержит заголовки</span>
                                </label>
                                <small className="checkboxHint">
                                    Отметьте, если первая строка в файле - это названия колонок (Фамилия, Имя и т.д.)
                                </small>
                            </div>

                            <label className="uploadButton">
                                <FiUpload />
                                <span>Выбрать файл Excel</span>
                                <input 
                                    type="file" 
                                    accept=".xlsx,.xls"
                                    onChange={handleFileUpload}
                                    style={{ display: 'none' }}
                                />
                            </label>
                        </div>
                    )}

                    {step === 2 && (
                        <div className="mappingSection">
                            <h3>Шаг 2: Укажите соответствие колонок</h3>
                            <p className="mappingHint">Выберите, какая колонка в вашем файле соответствует каждому полю</p>
                            
                            <div className="mappingFields">
                                <div className="mappingField required">
                                    <label>Фамилия *</label>
                                    <select 
                                        value={columnMapping.family}
                                        onChange={(e) => handleColumnMappingChange('family', e.target.value)}
                                    >
                                        <option value="">Выберите колонку</option>
                                        {headers.map(header => (
                                            <option key={header.index} value={header.index}>
                                                {header.letter}: {header.value}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div className="mappingField required">
                                    <label>Имя *</label>
                                    <select 
                                        value={columnMapping.name}
                                        onChange={(e) => handleColumnMappingChange('name', e.target.value)}
                                    >
                                        <option value="">Выберите колонку</option>
                                        {headers.map(header => (
                                            <option key={header.index} value={header.index}>
                                                {header.letter}: {header.value}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div className="mappingField">
                                    <label>Отчество</label>
                                    <select 
                                        value={columnMapping.father_name}
                                        onChange={(e) => handleColumnMappingChange('father_name', e.target.value)}
                                    >
                                        <option value="">Выберите колонку</option>
                                        {headers.map(header => (
                                            <option key={header.index} value={header.index}>
                                                {header.letter}: {header.value}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div className="mappingField required">
                                    <label>Дата рождения *</label>
                                    <select 
                                        value={columnMapping.birth_date}
                                        onChange={(e) => handleColumnMappingChange('birth_date', e.target.value)}
                                    >
                                        <option value="">Выберите колонку</option>
                                        {headers.map(header => (
                                            <option key={header.index} value={header.index}>
                                                {header.letter}: {header.value}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div className="mappingField">
                                    <label>Игровой номер</label>
                                    <select 
                                        value={columnMapping.number}
                                        onChange={(e) => handleColumnMappingChange('number', e.target.value)}
                                    >
                                        <option value="">Выберите колонку</option>
                                        {headers.map(header => (
                                            <option key={header.index} value={header.index}>
                                                {header.letter}: {header.value}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div className="mappingField">
                                    <label>Позиция</label>
                                    <select 
                                        value={columnMapping.position}
                                        onChange={(e) => handleColumnMappingChange('position', e.target.value)}
                                    >
                                        <option value="">Выберите колонку</option>
                                        {headers.map(header => (
                                            <option key={header.index} value={header.index}>
                                                {header.letter}: {header.value}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            <div className="mappingPreview">
                                <h4>Предпросмотр данных (первые 3 строки):</h4>
                                <div className="previewTable">
                                    {excelData.slice(0, 3).map((row, index) => (
                                        <div key={index} className="previewRow">
                                            <strong>Строка {index + 1}:</strong>
                                            {columnMapping.family && <span>Фамилия: {row[columnMapping.family]}</span>}
                                            {columnMapping.name && <span>Имя: {row[columnMapping.name]}</span>}
                                            {columnMapping.father_name && <span>Отчество: {row[columnMapping.father_name]}</span>}
                                            {columnMapping.birth_date && (() => {
                                                const iso = parseBirthday(row[columnMapping.birth_date], { date1904 });
                                                return iso
                                                    ? <span>Д.р.: {formatRuDate(iso)}</span>
                                                    : <span className="importDateBad">Д.р.: не распознана ({String(row[columnMapping.birth_date] ?? '')})</span>;
                                            })()}
                                            {columnMapping.number && <span>№: {row[columnMapping.number]}</span>}
                                            {columnMapping.position && <span>Поз.: {row[columnMapping.position]}</span>}
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <div className="mappingActions">
                                <button className="backButton" onClick={() => setStep(1)}>
                                    ← Назад
                                </button>
                                <button 
                                    className="nextButton" 
                                    onClick={handleProcessPlayers}
                                    disabled={isProcessing}
                                >
                                    {isProcessing
                                        ? `Проверка по базе${progress ? ` ${progress.done} из ${progress.total}` : ''}...`
                                        : 'Далее →'}
                                </button>
                            </div>
                        </div>
                    )}

                    {step === 3 && (() => {
                        const rowErrors = processedPlayers.map((row, index) => validateRow(row, index, processedPlayers));
                        const pendingCount = processedPlayers.filter(p => p.isSelected && !p.added).length;
                        const invalidCount = rowErrors.filter(errors => errors.length > 0).length;
                        const newCount = processedPlayers.filter(p => p.choice === 'new' && !p.added).length;
                        const photoSrc = (path) => (/^(data:|https?:|blob:)/.test(path) ? path : BASE_URL.url + path);

                        return (
                        <div className="reviewSection">
                            <h3>Шаг 3: Проверка и подтверждение</h3>
                            <p className="reviewHint">
                                <FiCheckCircle style={{color: '#38a169'}} /> - выбран игрок из базы |
                                <FiAlertCircle style={{color: '#d69e2e'}} /> - будет создан новый
                            </p>
                            <p className="importReviewNote">
                                Совпадение фамилии и имени ещё не значит, что это тот же человек:
                                сверьте дату рождения и отчество, при необходимости откройте карточку игрока.
                            </p>

                            {newCount > 0 && (
                                <div className="importBulkBar">
                                    <span>Позиция для всех новых ({newCount}):</span>
                                    <select
                                        value={bulkPosition}
                                        onChange={(e) => setBulkPosition(Number(e.target.value))}
                                        disabled={isAdding}
                                    >
                                        {POSITIONS.map(p => <option key={p.id} value={p.id}>{p.label}</option>)}
                                    </select>
                                    <button type="button" onClick={applyBulkPosition} disabled={isAdding}>
                                        Применить
                                    </button>
                                </div>
                            )}

                            {invalidCount > 0 && (
                                <div className="importErrorsSummary">
                                    Строк с ошибками: {invalidCount}. Исправьте их или снимите галочку —
                                    до этого добавление недоступно.
                                </div>
                            )}

                            <div className="playersReviewList">
                                {processedPlayers.map((player, index) => {
                                    const errors = rowErrors[index];
                                    const isNew = player.choice === 'new';
                                    const candidates = player.candidates || [];
                                    const chosen = isNew ? null : candidates.find(c => c.id === Number(player.choice));
                                    const photo = isNew ? player.draft?.photoPreview : chosen?.image;
                                    const shown = expanded[player.key] ? candidates : candidates.slice(0, CANDIDATES_SHOWN);
                                    const locked = player.added || isAdding;
                                    const numberBad = errors.some(e => e.startsWith('номер'));
                                    const draft = player.draft;
                                    const draftSummary = draft ? [
                                        draft.photoFile ? 'фото' : null,
                                        draft.height ? `${draft.height} см` : null,
                                        draft.weight ? `${draft.weight} кг` : null,
                                        draft.working_foot ? `нога: ${draft.working_foot}` : null,
                                        draft.nationality || null,
                                    ].filter(Boolean).join(' · ') : '';

                                    return (
                                    <div
                                        key={player.key}
                                        className={`playerReviewCard importRowCard ${isNew ? 'new' : 'existing'} ${!player.isSelected ? 'disabled' : ''} ${player.added ? 'importRowAdded' : ''} ${errors.length ? 'importRowInvalid' : ''}`}
                                    >
                                        <div className="importRowMain">
                                            <div className="playerReviewCheckbox">
                                                <input
                                                    type="checkbox"
                                                    checked={player.isSelected}
                                                    disabled={locked}
                                                    onChange={() => handleTogglePlayer(index)}
                                                />
                                            </div>
                                            <div className="playerReviewPhoto">
                                                {photo ? (
                                                    <img
                                                        src={photoSrc(photo)}
                                                        alt={`${player.family} ${player.name}`}
                                                        onError={(e) => e.target.style.display = 'none'}
                                                    />
                                                ) : null}
                                            </div>
                                            <div className="playerReviewInfo">
                                                <div className="playerReviewName">
                                                    {isNew ? <FiAlertCircle /> : <FiCheckCircle />}
                                                    <strong>
                                                        {chosen
                                                            ? `${chosen.family} ${chosen.name} ${chosen.father_name}`
                                                            : `${player.family} ${player.name} ${player.father_name}`}
                                                    </strong>
                                                    <span className="importRowExcel">строка {player.excelRow}</span>
                                                </div>
                                                <div className="playerReviewDetails">
                                                    {chosen ? (
                                                        <span>Д.р.: {chosen.birthday ? formatRuDate(chosen.birthday) : 'не указана'}</span>
                                                    ) : player.birth_date ? (
                                                        <span>Д.р.: {formatRuDate(player.birth_date)}</span>
                                                    ) : (
                                                        <span className="importDateBad">Д.р.: не распознана ({String(player.birth_date_raw ?? '')})</span>
                                                    )}
                                                    <span>
                                                        {chosen
                                                            ? `Из базы, id ${chosen.id}`
                                                            : player.createdPlayerId
                                                                ? `Создан, id ${player.createdPlayerId}`
                                                                : 'Будет создан новый игрок'}
                                                    </span>
                                                </div>
                                            </div>
                                            <div className="playerReviewNumber">
                                                <label>Номер:</label>
                                                <input
                                                    type="number"
                                                    value={player.editableNumber}
                                                    onChange={(e) => handleNumberChange(index, e.target.value)}
                                                    min="0"
                                                    max="99"
                                                    disabled={!player.isSelected || locked}
                                                    className={numberBad ? 'error' : ''}
                                                />
                                            </div>
                                        </div>

                                        {candidates.length > 0 && !player.added && (
                                            <div className="importCandidates">
                                                <div className="importCandidatesTitle">
                                                    В базе есть игроки с такими фамилией и именем ({candidates.length}).
                                                    Выберите нужного или создайте нового:
                                                </div>
                                                {shown.map(c => (
                                                    <label
                                                        key={c.id}
                                                        className={`importCandidate ${Number(player.choice) === c.id ? 'importCandidateOn' : ''}`}
                                                    >
                                                        <input
                                                            type="radio"
                                                            name={`importChoice-${player.key}`}
                                                            checked={Number(player.choice) === c.id}
                                                            disabled={locked}
                                                            onChange={() => updateRow(index, { choice: c.id })}
                                                        />
                                                        <span className="importCandidatePhoto">
                                                            {c.image ? (
                                                                <img
                                                                    src={photoSrc(c.image)}
                                                                    alt=""
                                                                    onError={(e) => e.target.style.display = 'none'}
                                                                />
                                                            ) : null}
                                                        </span>
                                                        <span className="importCandidateText">
                                                            <strong>{c.family} {c.name} {c.father_name}</strong>
                                                            <span className={c.birthdayMatch ? 'importMatch' : 'importMismatch'}>
                                                                д.р. {c.birthday ? formatRuDate(c.birthday) : 'не указана'}
                                                                {c.birthdayMatch ? ' — совпадает' : ''}
                                                            </span>
                                                            <span className="importCandidateId">id {c.id}</span>
                                                        </span>
                                                        <a
                                                            href={`/player/${c.id}`}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="importCandidateLink"
                                                        >
                                                            Открыть
                                                        </a>
                                                    </label>
                                                ))}
                                                {candidates.length > CANDIDATES_SHOWN && (
                                                    <button
                                                        type="button"
                                                        className="importLinkButton"
                                                        onClick={() => setExpanded(prev => ({ ...prev, [player.key]: !prev[player.key] }))}
                                                    >
                                                        {expanded[player.key] ? 'Свернуть' : `Показать всех (${candidates.length})`}
                                                    </button>
                                                )}
                                                <label className={`importCandidate ${isNew ? 'importCandidateOn' : ''}`}>
                                                    <input
                                                        type="radio"
                                                        name={`importChoice-${player.key}`}
                                                        checked={isNew}
                                                        disabled={locked}
                                                        onChange={() => updateRow(index, { choice: 'new' })}
                                                    />
                                                    <span className="importCandidateText">
                                                        <strong>Создать нового игрока</strong>
                                                        <span>ни один из найденных не подходит</span>
                                                    </span>
                                                </label>
                                            </div>
                                        )}

                                        {isNew && !player.added && (
                                            <div className="importNewTools">
                                                <label>
                                                    Позиция:
                                                    <select
                                                        value={player.position_id}
                                                        disabled={locked}
                                                        onChange={(e) => updateRow(index, { position_id: Number(e.target.value) })}
                                                    >
                                                        {POSITIONS.map(p => <option key={p.id} value={p.id}>{p.label}</option>)}
                                                    </select>
                                                </label>
                                                <button
                                                    type="button"
                                                    className="importCardButton"
                                                    disabled={locked}
                                                    onClick={() => openCard(index)}
                                                >
                                                    {draft ? 'Изменить карточку' : 'Заполнить карточку: фото, рост, вес…'}
                                                </button>
                                                {draftSummary && <span className="importDraftSummary">{draftSummary}</span>}
                                            </div>
                                        )}

                                        {player.checkFailed && !player.added && (
                                            <div className="playerReviewCheckFailed">
                                                Не удалось проверить по базе: {player.checkError}.
                                                Чтобы не создать дубликат, проверьте ещё раз.{' '}
                                                <button
                                                    type="button"
                                                    className="importLinkButton"
                                                    disabled={player.rechecking || locked}
                                                    onClick={() => handleRecheck(index)}
                                                >
                                                    {player.rechecking ? 'Проверяем...' : 'Проверить ещё раз'}
                                                </button>
                                            </div>
                                        )}

                                        {errors.length > 0 && (
                                            <ul className="importRowErrors">
                                                {errors.map(error => <li key={error}>{error}</li>)}
                                            </ul>
                                        )}
                                        {player.addError && (
                                            <div className="importRowErrors">Не добавлен: {player.addError}</div>
                                        )}
                                        {player.added && (
                                            <div className="importRowDone">✓ Добавлен в заявку</div>
                                        )}
                                    </div>
                                    );
                                })}
                            </div>

                            <div className="reviewActions">
                                <button className="backButton" onClick={() => setStep(2)} disabled={isAdding}>
                                    ← Назад
                                </button>
                                <button
                                    className="addPlayersButton"
                                    onClick={handleAddPlayers}
                                    disabled={isAdding || pendingCount === 0 || invalidCount > 0}
                                >
                                    {isAdding
                                        ? `Добавление${progress ? ` ${progress.done + 1} из ${progress.total}` : ''}...`
                                        : `Добавить игроков: ${pendingCount}`}
                                </button>
                            </div>
                        </div>
                        );
                    })()}
                </div>
            </div>
        </div>

        {card && (
            <EditPlayerModal
                isOpen
                onClose={() => setCard(null)}
                onSuccess={() => {}}
                isCreateMode
                userData={userData}
                initialData={card.initialData}
                onDraftSave={handleDraftSave}
            />
        )}
        </>
    );
}
